import { useState } from "react";
import { AlertTriangle, Loader2, ShoppingCart } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { formatarQuantidade } from "@/lib/quantidade";
import { formatPriceCents } from "@/components/services/service-meta";

const PERIODOS = [30, 60, 90] as const;

/**
 * O que comprar — a primeira tela do estoque, e de propósito.
 *
 * Um módulo de estoque clássico começa pelo inventário, e é exatamente aí que a planilha do Jun
 * parou: ele preencheu saldo em 6 de 31 insumos e desistiu. Esta tela responde sem inventário
 * nenhum, porque o consumo dos serviços concluídos já diz o que está girando. O saldo entra como
 * refinamento, não como pré-requisito.
 */
export function SugestaoDeCompra({ onComprar }: { onComprar: (materialLineId: string, sugerido: number) => void }) {
  const [dias, setDias] = useState<(typeof PERIODOS)[number]>(30);
  const query = trpc.estoque.sugestaoDeCompra.useQuery({ dias });

  if (query.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-brand-600" />
      </div>
    );
  }
  const dados = query.data;
  if (!dados) return null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1 rounded-lg border border-zinc-200 bg-zinc-50 p-0.5 dark:border-border dark:bg-muted/40">
          {PERIODOS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setDias(p)}
              className={cn(
                "h-7 rounded-md px-2.5 text-xs font-semibold transition-colors",
                dias === p
                  ? "bg-brand-600 text-white shadow-sm"
                  : "text-zinc-600 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground",
              )}
            >
              {p} dias
            </button>
          ))}
        </div>
        {dados.semMovimento > 0 && (
          <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">
            {dados.semMovimento} de {dados.totalDeMateriais} materiais ainda não têm nenhum lançamento — a
            conta melhora conforme o estoque for sendo usado.
          </p>
        )}
      </div>

      {dados.itens.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-300 py-10 text-center dark:border-border">
          <p className="text-sm text-zinc-500 dark:text-muted-foreground">Nada para comprar por enquanto.</p>
          <p className="mt-1 text-[11px] text-zinc-400 dark:text-muted-foreground/70">
            A sugestão aparece quando um material fica negativo, abaixo do mínimo, ou quando o consumo
            dos últimos {dias} dias passa do que há em estoque.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-xs">
            <thead className="border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500 dark:border-border dark:text-muted-foreground">
              <tr>
                <th className="pb-1.5 pr-2 text-left font-semibold">Insumo</th>
                <th className="w-20 pb-1.5 pr-2 text-right font-semibold">Saldo</th>
                <th className="w-24 pb-1.5 pr-2 text-right font-semibold">Consumo</th>
                <th className="w-24 pb-1.5 pr-2 text-right font-semibold">Acaba em</th>
                <th className="w-24 pb-1.5 pr-2 text-right font-semibold">Comprar</th>
                <th className="w-24 pb-1.5 pr-2 text-right font-semibold">Custo</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {dados.itens.map((i: any) => (
                <tr key={i.material.id} className="border-b border-zinc-100 last:border-b-0 dark:border-border/50">
                  <td className="py-2 pr-2">
                    <span className="block truncate text-xs text-zinc-900 dark:text-foreground">{i.material.name}</span>
                    <span className="flex flex-wrap gap-1.5 text-[10px] text-zinc-400 dark:text-muted-foreground/70">
                      <span>{i.material.manufacturerName}</span>
                      {i.material.subcategory && <span>· {i.material.subcategory.label}</span>}
                      {i.material.supplier && <span>· compra de {i.material.supplier.name}</span>}
                    </span>
                  </td>
                  <td
                    className={cn(
                      "py-2 pr-2 text-right tabular-nums",
                      i.negativo ? "font-semibold text-red-600 dark:text-red-400" : "text-zinc-700 dark:text-foreground/80",
                    )}
                  >
                    {formatarQuantidade(i.saldo)}
                    {i.material.unit && <span className="ml-0.5 text-zinc-400">{i.material.unit}</span>}
                  </td>
                  <td className="py-2 pr-2 text-right tabular-nums text-zinc-600 dark:text-muted-foreground">
                    {i.consumo > 0 ? formatarQuantidade(i.consumo) : "—"}
                  </td>
                  <td className="py-2 pr-2 text-right">
                    {i.negativo ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 dark:text-red-400">
                        <AlertTriangle className="h-3 w-3" />
                        negativo
                      </span>
                    ) : i.diasDeCobertura == null ? (
                      <span className="text-zinc-300 dark:text-muted-foreground/50">—</span>
                    ) : (
                      <span
                        className={cn(
                          "text-[11px] font-medium tabular-nums",
                          i.diasDeCobertura < 7 ? "text-amber-600 dark:text-amber-400" : "text-zinc-600 dark:text-muted-foreground",
                        )}
                      >
                        ~{Math.floor(i.diasDeCobertura)} dias
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-2 text-right font-semibold tabular-nums text-zinc-900 dark:text-foreground">
                    {i.sugerido > 0 ? formatarQuantidade(i.sugerido) : "—"}
                  </td>
                  <td className="py-2 pr-2 text-right tabular-nums text-zinc-600 dark:text-muted-foreground">
                    {i.custoSugeridoCents != null && i.sugerido > 0 ? formatPriceCents(i.custoSugeridoCents) : "—"}
                  </td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      onClick={() => onComprar(i.material.id, i.sugerido)}
                      title={`Lançar compra de ${i.material.name}`}
                      aria-label={`Lançar compra de ${i.material.name}`}
                      className="rounded p-1 text-zinc-400 hover:bg-brand-50 hover:text-brand-600 dark:hover:bg-primary/10 dark:hover:text-primary"
                    >
                      <ShoppingCart className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[10.5px] leading-snug text-zinc-500 dark:text-muted-foreground">
        A sugestão repõe o consumo do período, ou o que falta para o mínimo — o que for maior. O
        consumo vem dos serviços <strong>concluídos</strong>: trabalho em produção ainda não conta.
      </p>
    </div>
  );
}
